import { ContentState } from 'draft-js';
import type { LanguageToolMatch } from '../../services/LanguageTool';

export type BlockRange = {
    blockKey: string;
    start: number;
    end: number;
};

/**
 * Plain text used for LanguageTool must match Draft's getPlainText('\n'):
 * blocks joined by a single newline (no trailing newline after the last block).
 */
export const contentStateToPlainText = (contentState: ContentState): string => {
    return contentState.getPlainText('\n');
};

type OffsetOptions = {
    /** When true, an offset on a block boundary maps to the exclusive end of the previous block. */
    exclusiveEnd?: boolean;
};

/**
 * Map a LanguageTool absolute offset to a Draft block + local offset.
 * Returns null when the offset falls on a block separator (unless exclusiveEnd).
 */
export const absoluteOffsetToBlockPosition = (
    contentState: ContentState,
    absoluteOffset: number,
    options: OffsetOptions = {},
): { blockKey: string; offset: number } | null => {
    if (absoluteOffset < 0)
        return null;

    let cursor = 0;
    const blocks = contentState.getBlocksAsArray();

    for (let i = 0; i < blocks.length; i++) {
        const block = blocks[i];
        const len = block.getLength();
        const blockStart = cursor;
        const blockEnd = cursor + len;

        if (
            absoluteOffset < blockEnd ||
            (absoluteOffset === blockEnd && (options.exclusiveEnd || i === blocks.length - 1))
        ) {
            return {
                blockKey: block.getKey(),
                offset: absoluteOffset - blockStart,
            };
        }

        cursor = blockEnd;
        if (i < blocks.length - 1) {
            // The '\n' separator occupies one absolute index.
            if (absoluteOffset === cursor) {
                return options.exclusiveEnd
                    ? { blockKey: block.getKey(), offset: len }
                    : null;
            }
            cursor += 1;
        }
    }

    return null;
};

/**
 * Convert a LanguageTool match to a range within a specific Draft block.
 * Returns null when the match does not intersect that block.
 */
export const matchToBlockRange = (
    contentState: ContentState,
    match: LanguageToolMatch,
    blockKey: string,
): BlockRange | null => {
    if (!match || match.length <= 0)
        return null;

    const startPos = absoluteOffsetToBlockPosition(contentState, match.offset);
    if (!startPos || startPos.blockKey !== blockKey)
        return null;

    const endPos = absoluteOffsetToBlockPosition(
        contentState,
        match.offset + match.length,
        { exclusiveEnd: true },
    );

    let endOffset: number;
    if (endPos && endPos.blockKey === blockKey) {
        endOffset = endPos.offset;
    } else {
        const block = contentState.getBlockForKey(blockKey);
        endOffset = block ? block.getLength() : startPos.offset;
    }

    if (endOffset <= startPos.offset)
        return null;

    return {
        blockKey,
        start: startPos.offset,
        end: endOffset,
    };
};

/**
 * Find the LanguageTool match that covers a decorated Draft range.
 */
export const findMatchForDecoration = (
    contentState: ContentState,
    matches: LanguageToolMatch[],
    blockKey: string,
    start: number,
    end: number,
): LanguageToolMatch | null => {
    for (const match of matches) {
        const range = matchToBlockRange(contentState, match, blockKey);
        if (range && range.start === start && range.end === end)
            return match;
    }
    return null;
};
